import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { saveAs } from 'file-saver';
import '../../../Assets/font/fonts.css';
import {
  CertificateV2,
  CertificateStatus,
  LanguageComponent,
  PhotoAndSignatureModelWithUrl,
} from '@sdir/httpclient/lib/clients/aps/personalcertificate';
import {
  AttachmentResponse as AddresseeAttachmentResponse,
  AttachmentType as AddresseeAttachmentType,
} from '@sdir/httpclient/lib/clients/core/commonaddressee';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { Certificate as CertificateCard } from '@sdir/blueprint.aps/lib/components';
import { CertificateProps } from '@sdir/blueprint.aps/lib/components/Organisms/Certificate';
import { Loader, SdirButton } from '@sdir/sds';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { ApplicationLayout, CertificateActions } from '../../Organisms';
import { personalCertificateApi } from '../../../httpclient';
import { LoadWrapper } from '../../Atoms/LoadWrapper';
import { useAddresseeAttachment, useCertificateCodes } from '../../../services/hooks';
import { CapitalizeString } from '../../../helpers/Certificates';
import { ToggleContent } from '../../Atoms/ToggleContent';
import { ToggleDisablePrinting } from '../../../constants/permissions';
import { DEFAULT_LANG } from '../../../helpers/languageHelpers';

const asAddresseeAttachment = (
  data: PhotoAndSignatureModelWithUrl | null | undefined,
  type: AddresseeAttachmentType
): AddresseeAttachmentResponse | null => {
  if (!data) return null;
  return {
    attachmentType: type,
    blobName: data.blobName,
    uid: data.blobUid,
  };
};

const CertificatePage: React.FC = () => {
  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { certificateUid } = useParams<{ certificateUid: string }>();
  const [currentCertificate, setCurrentCertificate] = useState<CertificateV2>();
  const [certificateLanguage, setCertificateLanguage] = useState<string>();

  const { result, loading, error, callApi } = useApi({
    apiFunction: (uid, options = {}) =>
      personalCertificateApi.v2PersonalcertificateGetpersonalcertificatev2UidGet(
        uid || certificateUid || '',
        options
      ),
    callApiOnInit: true,
  });

  const { result: ownRenewals, loading: ownRenewalsLoading } = useApi({
    apiFunction: () => personalCertificateApi.v1PersonalcertificateGetownavailablerenewalsGet(),
    callApiOnInit: true,
  });

  const { loading: loadingPdf, callApi: generatePdf } = useApi({
    apiFunction: (uid) =>
      personalCertificateApi.v1PersonalcertificateGeneratepersonalcertificatepdfAddWatermarkGet(
        uid,
        true,
        {
          headers: { 'Accept-Language': certificateLanguage || intl?.locale || DEFAULT_LANG },
          responseType: 'blob',
        }
      ),
    callApiOnInit: false,
    successCallback: (response: any) => {
      const blob = new Blob([response], { type: 'application/octet-stream' });
      const certNr = result?.certificateNumber;
      const fileName = `Certificate${certNr ? `_${certNr}` : ''}.pdf`;
      saveAs(blob, fileName);
    },
  });

  const isValidForRenewal = useMemo(() => {
    const isInactive = result?.status === CertificateStatus.Inactive;
    if (isInactive) return false;
    return ownRenewals?.some((c) => {
      const [code, type] = c?.certificateCode?.split('_') as string[];
      return !!(code === result?.certificateType || c.certificateCode === result?.certificateType);
    });
  }, [ownRenewals, result?.certificateType]);

  const photoAttachment = useMemo(
    () => asAddresseeAttachment(result?.photo, AddresseeAttachmentType.Photo),
    [result?.photo]
  );

  const signatureAttachment = useMemo(
    () => asAddresseeAttachment(result?.signature, AddresseeAttachmentType.Signature),
    [result?.signature]
  );

  const photoFile = useAddresseeAttachment(photoAttachment || null);
  const signatureFile = useAddresseeAttachment(signatureAttachment || null);

  const codes = useCertificateCodes();

  useEffect(() => {
    if (codes && codes.result && result && result.certificateType) {
      const certificate = codes.result.find(
        (certificates) => certificates.code === result.certificateType
      );
      if (certificate) setCurrentCertificate(certificate);
    }
  }, [codes, result]);

  const getLocalizedString = useCallback(
    (langObj: { [key: string]: string } | LanguageComponent | null | undefined) => {
      if (langObj) {
        const langKey = intl.locale || DEFAULT_LANG;
        return langObj[langKey] || langObj.no || '';
      }
      return '';
    },
    [intl.locale]
  );

  const Remap = useCallback(
    (list) => {
      const resultList: Array<string> = new Array<string>();
      list?.forEach((item) => {
        if (item !== undefined) {
          resultList.push(getLocalizedString(item) || '');
        }
      });
      return resultList;
    },
    [getLocalizedString]
  );

  const onChangeLanguage = (newLang: string) => {
    const headerLang = newLang === 'no' ? DEFAULT_LANG : newLang;
    setCertificateLanguage(headerLang);
    callApi(certificateUid || '', { headers: { 'Accept-Language': headerLang } });
  };

  const onDownloadPdf = () => {
    generatePdf(certificateUid);
  };

  const certificateCard = useMemo((): CertificateProps | null => {
    if (!result) return null;
    const positionList =
      result?.limitations?.map((l) => {
        return {
          position: l.capacity || '',
          limitations: l.limit ? [CapitalizeString(l.limit)] : [],
        };
      }) || [];
    return {
      name: `${result?.firstName} ${result?.middleName || ''} ${result?.lastName}` || '',
      authorizedOfficial: result?.authorizedOfficial || '',
      issuingAuthority: result?.issuingAuthority,
      dateOfBirth: result?.dateOfBirth || '',
      certificateNr:
        !result?.certificateNumber || result?.certificateNumber === '0'
          ? '-'
          : result?.certificateNumber,
      certificateName: CapitalizeString(result?.certificateName || ''),
      issuedDate: result?.issuedDate,
      validFrom: result?.validFromDate,
      minimumRequirements: result?.minimumRequirements || '',
      accessibilityStatement: result?.accessibilityRequirement || '',
      limitationStatement: result?.capacityLimitationStatement || '',
      meetsRequirementStatement: result?.meetsRequirementUntil || '',
      positionList,
      hideLanguageSelector: false,
      qrData: `${process.env.REACT_APP_VERIFY_URL}/certificate/${certificateUid}`,
      qrImageLoading: false,
      onChangeLanguage,
    };
  }, [
    Remap,
    getLocalizedString,
    photoFile.loading,
    photoFile.result,
    result,
    signatureFile.loading,
    signatureFile.result,
    currentCertificate,
    certificateUid,
  ]);

  return (
    <ApplicationLayout
      topHeading=""
      heading={CapitalizeString(result?.certificateName || '')}
      exitLabel={intl.formatMessage({ id: 'general.form.back' })}
      mainContentMaxWidth="100%"
      hideNavigation
      largeGap
      sideContent={
        <ToggleContent toggleName={ToggleDisablePrinting} showLoader inverse>
          <LoadWrapper loading={loading || ownRenewalsLoading} error={error}>
            {result && (
              <CertificateActions
                certificate={result}
                isValidForRenewal={isValidForRenewal}
                isValidForDuplicate={false}
              />
            )}
          </LoadWrapper>
        </ToggleContent>
      }
    >
      {loading && <Loader />}
      {error && <ErrorMessage error={error} />}
      {!loading && certificateUid && (
        <ActionWrapper>
          <SdirButton
            onClick={onDownloadPdf}
            text={intl.formatMessage({ id: 'general.button.downloadpdf' })}
            disabled={loadingPdf}
          />
          {loadingPdf && <Loader size="30px" />}
        </ActionWrapper>
      )}
      {certificateCard && (
        <CertificateWrapper translate="no">
          <CertificateCard {...certificateCard} />
        </CertificateWrapper>
      )}
    </ApplicationLayout>
  );
};
export default CertificatePage;

const CertificateWrapper = styled.div`
  p {
    margin: 0;
  }

  margin-bottom: 20px;
`;

const ActionWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  button {
    margin-right: 20px;
  }
`;
